/**
 * Workspace Team Context
 *
 * Manages workspace-level team state including members, invitations,
 * and team operations. Uses TanStack Query for caching and refetching.
 * 
 * ACCESS MODEL (simplified):
 * - Workspace membership grants access to ALL properties attached to that workspace
 * - No per-user property allowlists
 * - Seats are enforced account-wide
 */

import {
  createContext,
  useContext,
  useCallback,
  useMemo,
} from "react"
import type { ReactNode } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { useAuth } from "./AuthContext"
import { useWorkspace } from "./WorkspaceContext"
import { useSubscription } from "./SubscriptionContext"
import type {
  WorkspaceMember,
  WorkspaceInvite,
  WorkspaceTeamStats,
  WorkspaceRole,
} from "@/lib/types/workspace-team"
import {
  getWorkspaceMembers,
  getWorkspaceInvites,
  inviteToWorkspace,
  removeFromWorkspace,
  resendWorkspaceInvite,
  cancelWorkspaceInvite,
  updateMemberRole,
} from "@/lib/api/workspace-team"

interface WorkspaceTeamContextType {
  // Data
  members: WorkspaceMember[]
  invites: WorkspaceInvite[]
  stats: WorkspaceTeamStats | null

  // Loading states
  isLoading: boolean
  isMembersLoading: boolean
  isInvitesLoading: boolean

  // Error states
  error: Error | null

  // Permissions
  isOwner: boolean
  isAdmin: boolean
  hasTeamAccess: boolean
  canInvite: boolean
  canManageMembers: boolean

  // Actions
  inviteMember: (email: string, role?: WorkspaceRole) => Promise<void>
  removeMember: (userId: string) => Promise<void>
  resendInvite: (inviteId: string) => Promise<void>
  cancelInvite: (inviteId: string) => Promise<void>
  updateRole: (userId: string, role: WorkspaceRole) => Promise<void>
  refetch: () => void
}

const WorkspaceTeamContext = createContext<WorkspaceTeamContextType | undefined>(
  undefined
)

export function WorkspaceTeamProvider({ children }: { children: ReactNode }) {
  const { token, user } = useAuth()
  const { workspace } = useWorkspace()
  const subscriptionContext = useSubscription()
  const queryClient = useQueryClient()

  const workspaceId = workspace?.workspaceId

  // Fetch members
  const {
    data: membersData,
    isLoading: isMembersLoading,
    error: membersError,
  } = useQuery({
    queryKey: ["workspace-members", workspaceId],
    queryFn: () => getWorkspaceMembers(workspaceId!, token!),
    enabled: !!workspaceId && !!token,
    staleTime: 2 * 60 * 1000, // 2 minutes
  })

  // Fetch invites
  const {
    data: invitesData,
    isLoading: isInvitesLoading,
    error: invitesError,
  } = useQuery({
    queryKey: ["workspace-invites", workspaceId],
    queryFn: () => getWorkspaceInvites(workspaceId!, token!),
    enabled: !!workspaceId && !!token,
    staleTime: 2 * 60 * 1000, // 2 minutes
  })

  const members = membersData?.members || []
  const invites = invitesData?.invites || []

  // Calculate stats (account-wide seat counting)
  const stats = useMemo((): WorkspaceTeamStats | null => {
    if (!workspaceId) return null

    // Access teamSeats from features (from SubscriptionState -> features: FeatureAccess)
    const seatLimit = subscriptionContext?.features?.teamSeats || 1
    const isUnlimited = seatLimit === 999999999
    const usedSeats = members.length + invites.length
    const availableSeats = isUnlimited
      ? Infinity
      : Math.max(0, seatLimit - usedSeats)

    return {
      total_members: members.length,
      pending_invites: invites.length,
      seat_limit: seatLimit,
      is_unlimited: isUnlimited,
      used_seats: usedSeats,
      available_seats: isUnlimited ? Infinity : availableSeats,
    }
  }, [members.length, invites.length, subscriptionContext?.features?.teamSeats, workspaceId])

  // Get current user's membership
  const currentMember = useMemo(() => {
    if (!user?.email || !members.length) return null
    return members.find(
      (m) => m.email.toLowerCase() === user.email?.toLowerCase()
    )
  }, [user?.email, members])

  // Check if current user is owner
  const isOwner = currentMember?.is_owner || currentMember?.role === 'owner' || false

  // Check if current user is admin
  const isAdmin = currentMember?.role === 'admin' || false

  // Check team access from subscription
  const hasTeamAccess = useMemo(() => {
    if (!subscriptionContext) return false
    const teamSeats = subscriptionContext.features?.teamSeats || 0
    const teamAccess = subscriptionContext.features?.teamAccess || false
    return teamSeats > 1 || teamAccess === true
  }, [subscriptionContext?.features?.teamSeats, subscriptionContext?.features?.teamAccess])

  // Can invite if owner/admin, has team access, and seats available.
  // A canceled subscription keeps `features.teamAccess` (the member list stays
  // readable) so the seat math alone would leave Invite live on a churned Scale
  // account — adding a seat is creation, and creation is what a cancel closes.
  const canInvite = useMemo(() => {
    if (!isOwner && !isAdmin) return false
    if (subscriptionContext?.isReadOnly) return false
    if (!hasTeamAccess) return false
    if (!stats) return false
    return stats.is_unlimited || stats.available_seats > 0
  }, [isOwner, isAdmin, subscriptionContext?.isReadOnly, hasTeamAccess, stats])

  // Can manage members (remove, change role) if owner or admin
  const canManageMembers = isOwner || isAdmin

  // Invalidate queries helper
  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["workspace-members", workspaceId] })
    queryClient.invalidateQueries({ queryKey: ["workspace-invites", workspaceId] })
  }, [queryClient, workspaceId])

  // Invite mutation
  const inviteMutation = useMutation({
    mutationFn: async ({
      email,
      role = 'member',
    }: {
      email: string
      role?: WorkspaceRole
    }) => {
      if (!workspaceId || !token) throw new Error("Missing workspace or auth")
      return inviteToWorkspace(workspaceId, email, role, token)
    },
    onSuccess: () => {
      toast.success("Invitation sent successfully")
      invalidateQueries()
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to send invitation")
    },
  })

  // Remove member mutation
  const removeMutation = useMutation({
    mutationFn: async (userId: string) => {
      if (!workspaceId || !token) throw new Error("Missing workspace or auth")
      return removeFromWorkspace(workspaceId, userId, token)
    },
    onSuccess: () => {
      toast.success("Member removed successfully")
      invalidateQueries()
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to remove member")
    },
  })

  // Resend invite mutation
  const resendMutation = useMutation({
    mutationFn: async (inviteId: string) => {
      if (!workspaceId || !token) throw new Error("Missing workspace or auth")
      return resendWorkspaceInvite(workspaceId, inviteId, token)
    },
    onSuccess: () => {
      toast.success("Invitation resent successfully")
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to resend invitation")
    },
  })

  // Cancel invite mutation
  const cancelMutation = useMutation({
    mutationFn: async (inviteId: string) => {
      if (!workspaceId || !token) throw new Error("Missing workspace or auth")
      return cancelWorkspaceInvite(workspaceId, inviteId, token)
    },
    onSuccess: () => {
      toast.success("Invitation cancelled")
      invalidateQueries()
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to cancel invitation")
    },
  })

  // Update role mutation
  const updateRoleMutation = useMutation({
    mutationFn: async ({
      userId,
      role,
    }: {
      userId: string
      role: WorkspaceRole
    }) => {
      if (!workspaceId || !token) throw new Error("Missing workspace or auth")
      return updateMemberRole(workspaceId, userId, role, token)
    },
    onSuccess: () => {
      toast.success("Role updated successfully")
      invalidateQueries()
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update role")
    },
  })

  // Action wrappers
  const inviteMember = useCallback(
    async (email: string, role: WorkspaceRole = 'member') => {
      await inviteMutation.mutateAsync({ email, role })
    },
    [inviteMutation]
  )

  const removeMember = useCallback(
    async (userId: string) => {
      await removeMutation.mutateAsync(userId)
    },
    [removeMutation]
  )

  const resendInvite = useCallback(
    async (inviteId: string) => {
      await resendMutation.mutateAsync(inviteId)
    },
    [resendMutation]
  )

  const cancelInvite = useCallback(
    async (inviteId: string) => {
      await cancelMutation.mutateAsync(inviteId)
    },
    [cancelMutation]
  )

  const updateRole = useCallback(
    async (userId: string, role: WorkspaceRole) => {
      await updateRoleMutation.mutateAsync({ userId, role })
    },
    [updateRoleMutation]
  )

  const refetch = useCallback(() => {
    invalidateQueries()
  }, [invalidateQueries])

  const value: WorkspaceTeamContextType = {
    members,
    invites,
    stats,
    isLoading: isMembersLoading || isInvitesLoading,
    isMembersLoading,
    isInvitesLoading,
    error: membersError || invitesError || null,
    isOwner,
    isAdmin,
    hasTeamAccess,
    canInvite,
    canManageMembers,
    inviteMember,
    removeMember,
    resendInvite,
    cancelInvite,
    updateRole,
    refetch,
  }

  return (
    <WorkspaceTeamContext.Provider value={value}>
      {children}
    </WorkspaceTeamContext.Provider>
  )
}

export function useWorkspaceTeam() {
  const context = useContext(WorkspaceTeamContext)
  if (!context) {
    throw new Error("useWorkspaceTeam must be used within WorkspaceTeamProvider")
  }
  return context
}

// Safe hook that returns null if provider is not available
function useWorkspaceTeamSafe() {
  return useContext(WorkspaceTeamContext)
}
